/**
 * Facets (fleet filter contract v3 DRAFT, §16.3, Client Outreach is the named reference
 * implementation) — a choice/boolean column's option counts are the number of rows matching
 * EVERY active filter EXCEPT the one on its own column, over the whole matching set (never the
 * page). Reuses {@link applyClientFilters} verbatim: strip this column's own `filter.<key>.*`
 * entries, run the rest, then tally the result by this column's own `getValue`.
 *
 * No new engine, no new semantics -- a quick view or a future search `q` param is already a
 * `filter.*`/other key in the same flat record, so it is automatically "every active filter"
 * without this file knowing it exists.
 */
import { applyClientFilters } from './applyClientFilters';
import { filterPrefix } from './columnFilterOps';
import { FilterColumn } from './types';

/** Shared base-set computation: every active filter EXCEPT this column's own. */
function baseRows<T>(
  items: readonly T[],
  filters: Record<string, string>,
  columns: FilterColumn<T>[],
  targetColumn: FilterColumn<T>
): T[] {
  const prefix = filterPrefix(targetColumn.key);
  const othersFilters: Record<string, string> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (!k.startsWith(prefix)) othersFilters[k] = v;
  }
  return applyClientFilters(items as T[], othersFilters, columns);
}

export function computeFacetCounts<T>(
  items: readonly T[],
  filters: Record<string, string>,
  columns: FilterColumn<T>[],
  targetColumn: FilterColumn<T>
): Record<string, number> {
  const base = baseRows(items, filters, columns, targetColumn);

  const counts: Record<string, number> = {};
  const getValue = targetColumn.getValue ?? (() => undefined);
  for (const item of base) {
    const raw = getValue(item);
    let key: string | undefined;
    if (targetColumn.filterType === 'boolean') {
      key = raw === true ? 'true' : raw === false ? 'false' : undefined;
    } else if (targetColumn.filterType === 'picklist') {
      key = raw === null || raw === undefined ? undefined : String(raw);
    }
    if (key === undefined) continue; // no bucket for this row (matches the predicate's own null-skip)
    counts[key] = (counts[key] ?? 0) + 1;
  }
  return counts;
}

/**
 * Count of rows with NO value for `targetColumn`, over the same "every filter except its own
 * column" base set {@link computeFacetCounts} uses — the count behind the lookup combobox's
 * "(has no value)" option (fleet filter contract v3 §16.8 / C86). A SEPARATE function, not a key
 * folded into `computeFacetCounts`'s own return object, so adding it never changes that function's
 * return shape for a caller that doesn't know about it (computeFacetCounts's own tests assert exact
 * `toEqual` objects with no extra keys). Picklist/boolean only — other filterTypes have no
 * "no value" option on their controls and always return 0.
 */
export function computeNoValueCount<T>(
  items: readonly T[],
  filters: Record<string, string>,
  columns: FilterColumn<T>[],
  targetColumn: FilterColumn<T>
): number {
  if (targetColumn.filterType !== 'picklist' && targetColumn.filterType !== 'boolean') return 0;
  const base = baseRows(items, filters, columns, targetColumn);
  const getValue = targetColumn.getValue ?? (() => undefined);
  let count = 0;
  for (const item of base) {
    const raw = getValue(item);
    if (raw === null || raw === undefined) count++;
  }
  return count;
}
